import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import { useRef } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { forEach as _forEach, forIn as _forIn, includes as _includes, orderBy as _orderBy } from 'lodash';
import { utils } from '@gisatcz/ptr-utils';
import { Pane } from 'react-leaflet';
import helpers from './helpers';
import GeoJsonLayer from './GeoJsonLayer';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var SvgVectorLayer = function SvgVectorLayer(_ref) {
  var _data$polygons, _data$lines, _data$points;
  var opacity = _ref.opacity,
    layerKey = _ref.layerKey,
    uniqueLayerKey = _ref.uniqueLayerKey,
    features = _ref.features,
    fidColumnName = _ref.fidColumnName,
    omittedFeatureKeys = _ref.omittedFeatureKeys,
    selectable = _ref.selectable,
    selected = _ref.selected,
    hoverable = _ref.hoverable,
    hovered = _ref.hovered,
    style = _ref.style,
    pointAsMarker = _ref.pointAsMarker,
    onClick = _ref.onClick,
    withSelectedFeaturesOnly = _ref.withSelectedFeaturesOnly,
    resources = _ref.resources;
  var pointsPaneName = useRef(utils.uuid());
  var linesPaneName = useRef(utils.uuid());
  var polygonsPaneName = useRef(utils.uuid());
  var onFeatureClick = function onFeatureClick(fid, position) {
    if (onClick) {
      onClick(layerKey, [fid], position);
    }
  };
  var prepareData = function prepareData(features) {
    if (features) {
      var pointFeatures = [];
      var polygonFeatures = [];
      var lineFeatures = [];
      var sortedPointFeatures = null;
      var sortedPolygonFeatures = null;
      _forEach(features, function (feature) {
        var type = feature && feature.geometry && feature.geometry.type;
        if (type) {
          var _selectedLocal, _selectedLocal2;
          var fid = feature.id || fidColumnName && feature.properties[fidColumnName];
          var uniqueFeatureKey = "".concat(uniqueLayerKey, "_").concat(fid);
          var selectedLocal = null;
          var defaultStyle = null;
          if (selected && fid) {
            _forIn(selected, function (selection) {
              if (selection.keys && _includes(selection.keys, fid)) {
                selectedLocal = selection;
              }
            });
          }
          if (type === 'Point' || type === 'MultiPoint') {
            defaultStyle = helpers.getDefaultStyle(feature, style);
          }
          var _data = {
            feature: feature,
            fid: fid,
            uniqueFeatureKey: uniqueFeatureKey,
            defaultStyle: defaultStyle,
            selected: !!selectedLocal,
            selectedStyleDefinition: (_selectedLocal = selectedLocal) === null || _selectedLocal === void 0 ? void 0 : _selectedLocal.style,
            selectedHoveredStyleDefinition: (_selectedLocal2 = selectedLocal) === null || _selectedLocal2 === void 0 ? void 0 : _selectedLocal2.hoveredStyle
          };
          switch (type) {
            case 'Point':
            case 'MultiPoint':
              pointFeatures.push(_data);
              break;
            case 'Polygon':
            case 'MultiPolygon':
              polygonFeatures.push(_data);
              break;
            case 'LineString':
            case 'MultiLineString':
              lineFeatures.push(_data);
              break;
            default:
              break;
          }
        }
      });

      // sort point features by radius
      if (pointFeatures.length) {
        sortedPointFeatures = _orderBy(pointFeatures, ['defaultStyle.radius', 'fid'], ['desc', 'asc']);
      }

      // sort polygon features, if selected
      if (polygonFeatures.length) {
        if (selected) {
          sortedPolygonFeatures = _orderBy(polygonFeatures, ['selected'], ['asc']);
        } else {
          sortedPolygonFeatures = polygonFeatures;
        }
      }
      return {
        polygons: sortedPolygonFeatures,
        points: sortedPointFeatures || pointFeatures,
        lines: lineFeatures
      };
    } else {
      return null;
    }
  };
  var renderGeoJson = function renderGeoJson(features) {
    return /*#__PURE__*/_jsx(GeoJsonLayer, {
      layerKey: layerKey,
      uniqueLayerKey: uniqueLayerKey,
      paneName: pointsPaneName.current,
      features: features,
      onFeatureClick: onFeatureClick,
      omittedFeatureKeys: omittedFeatureKeys,
      fidColumnName: fidColumnName,
      pointAsMarker: pointAsMarker,
      selectable: selectable,
      hoverable: hoverable,
      styleDefinition: style,
      hoveredStyleDefinition: hovered && hovered.style,
      icons: resources === null || resources === void 0 ? void 0 : resources.icons
    });
  };
  var renderFeatures = function renderFeatures(features) {
    return renderGeoJson(features);
  };
  var data = prepareData(features);
  var paneStyle = opacity ? {
    opacity: opacity
  } : null;
  var classes = classnames({
    'hoverable-pane': hoverable,
    'selected-features-pane': withSelectedFeaturesOnly
  });
  return data ? /*#__PURE__*/_jsxs(_Fragment, {
    children: [(_data$polygons = data.polygons) !== null && _data$polygons !== void 0 && _data$polygons.length ? /*#__PURE__*/_jsx(Pane, {
      className: classes,
      style: _objectSpread(_objectSpread({}, paneStyle), {}, {
        zIndex: 1
      }),
      name: polygonsPaneName.current,
      children: renderFeatures(data.polygons)
    }) : null, (_data$lines = data.lines) !== null && _data$lines !== void 0 && _data$lines.length ? /*#__PURE__*/_jsx(Pane, {
      className: classes,
      style: _objectSpread(_objectSpread({}, paneStyle), {}, {
        zIndex: 2
      }),
      name: linesPaneName.current,
      children: renderFeatures(data.lines)
    }) : null, (_data$points = data.points) !== null && _data$points !== void 0 && _data$points.length ? /*#__PURE__*/_jsx(Pane, {
      className: classes,
      style: _objectSpread(_objectSpread({}, paneStyle), {}, {
        zIndex: 3
      }),
      name: pointsPaneName.current,
      children: renderFeatures(data.points)
    }) : null]
  }) : null;
};
SvgVectorLayer.propTypes = {
  layerKey: PropTypes.string,
  uniqueLayerKey: PropTypes.string,
  // typically a combination of layerKey and data source key (or just layerKey, if no data source)
  features: PropTypes.array,
  fidColumnName: PropTypes.string,
  omittedFeatureKeys: PropTypes.array,
  selectable: PropTypes.bool,
  selected: PropTypes.object,
  hoverable: PropTypes.bool,
  hovered: PropTypes.object,
  style: PropTypes.object,
  pointAsMarker: PropTypes.bool,
  onClick: PropTypes.func,
  withSelectedFeaturesOnly: PropTypes.bool,
  // True, if layer contains only selected features
  opacity: PropTypes.number,
  resources: PropTypes.object
};
export default SvgVectorLayer;